"use client";

import {
  IResource,
  Office,
  WorkplaceRequestElement,
  WorkplaceRequestReservationTicketWorkflow,
  WorkplaceRequestWorkflowOverrides
} from "@gettactic/api";
import { HookFormResourcePickerFieldInput } from "@gettactic/components/src/form/ResourcePickerFieldInput";
import { HookFormTextEditorInput } from "@gettactic/components/src/form/TextEditorInput";
import { HookFormTextInput } from "@gettactic/components/src/form/TextInput";
import { HookFormToggleInput } from "@gettactic/components/src/form/ToggleInput";
import { HookFormAsyncUserSelectInput } from "@gettactic/components/src/form/UserSelectInput";
import { Button } from "@gettactic/components/src/primitives/button";
import {
  Dialog,
  DialogContent
} from "@gettactic/components/src/primitives/Dialog/Dialog";
import { isWorkplaceRequestBufferEnabled } from "@gettactic/helpers/src/featureFlags";
import { isGuestAttendee } from "@gettactic/helpers/src/workplace-requests/reservationFieldValue";
import {
  useAuthenticated,
  useOfficesQuery,
  useResourcesByIds
} from "@gettactic/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Armchair } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import { ResourceFinderModal } from "@/features/map/components/modal/ResourceFinderModal";
import { ReservationAttendeesInput } from "@/features/workplace-requests/views/queue/steps/ReservationAttendeesInput";
import { ReservationPeriodsField } from "./ReservationPeriodsField";
import {
  formValuesToOverride,
  RESERVATION_BUFFER_MAX_MINUTES,
  ReservationOverrideFormValues,
  reservationOverrideSchema,
  resolvedToFormValues
} from "./reservationOverrideForm";

/** What the approver decided, sent as one approval. */
export type ApproveRequestSelection = {
  /** Approver edits to the reservations the workflows would book. */
  overrides: WorkplaceRequestWorkflowOverrides;
};

interface ApproveRequestModalProps {
  request: WorkplaceRequestElement;
  /** The enabled `reservations:create` workflows of the request's type. */
  workflows: WorkplaceRequestReservationTicketWorkflow[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selection: ApproveRequestSelection) => void;
  isSubmitting: boolean;
  /** Why the last confirm failed (a booking conflict, say); shown above the footer. */
  submitError?: string | null;
}

/**
 * The last step before an approval that books a reservation: the reservations
 * it would create, editable. Nothing is sent until the approver confirms, and
 * only the fields they changed go as overrides. Which follow-up requests the
 * approval files is decided earlier, in the approval box itself.
 */
export const ApproveRequestModal: React.FC<ApproveRequestModalProps> = ({
  request,
  workflows,
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
  submitError
}) => {
  const {
    userContext: { deviceTz, authenticatedUser }
  } = useAuthenticated();
  const fallbackTz = authenticatedUser?.user?.time_zone ?? deviceTz;
  const officesQuery = useOfficesQuery();
  const offices = officesQuery.data?.offices ?? [];
  const isBufferEnabled = !!isWorkplaceRequestBufferEnabled(authenticatedUser);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isSubmitting) onClose();
      }}
    >
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col gap-0 p-0">
        {isOpen ? (
          <ApprovalSections
            key={workflows.map((workflow) => workflow.id).join(",")}
            request={request}
            workflows={workflows}
            fallbackTz={fallbackTz}
            offices={offices}
            isBufferEnabled={isBufferEnabled}
            onClose={onClose}
            onConfirm={onConfirm}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

interface ApprovalSectionsProps
  extends Omit<ApproveRequestModalProps, "isOpen"> {
  /** Used when neither the resource's nor the request's office is known. */
  fallbackTz: string;
  offices: Office[];
  isBufferEnabled: boolean;
}

/**
 * The reservations the approval books, a form each. Every section owns its
 * `useForm` and registers a submit handle here; the footer asks each in turn
 * and only confirms when every one validates.
 */
const ApprovalSections: React.FC<ApprovalSectionsProps> = ({
  request,
  workflows,
  fallbackTz,
  offices,
  isBufferEnabled,
  onClose,
  onConfirm,
  isSubmitting,
  submitError
}) => {
  const sections = useRef(new Map<string, SectionHandle>());

  const register = (id: string, handle: SectionHandle | null) => {
    if (handle) sections.current.set(id, handle);
    else sections.current.delete(id);
  };

  const handleConfirm = async () => {
    const overrides: WorkplaceRequestWorkflowOverrides = {};
    let isValid = true;
    for (const workflow of workflows) {
      const handle = sections.current.get(workflow.id);
      if (!handle) continue;
      const result = await handle.submit();
      if (result === "invalid") {
        isValid = false;
        continue;
      }
      if (result) overrides[workflow.id] = result;
    }
    if (!isValid) return;
    onConfirm({ overrides });
  };

  return (
    <>
      <div className="border-neutral-100 border-b p-5">
        <h2 className="font-semibold text-lg text-neutral-900">
          Approve and book
        </h2>
        <p className="text-neutral-700 text-sm">
          Approving request #{request.display_id} books the reservation below.
          Adjust anything before confirming.
        </p>
      </div>

      <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-5">
        {workflows.map((workflow) => (
          <ReservationSection
            key={workflow.id}
            workflow={workflow}
            showHeading={workflows.length > 1}
            officeId={request.office_id}
            fallbackTz={fallbackTz}
            offices={offices}
            isBufferEnabled={isBufferEnabled}
            onRegister={(handle) => register(workflow.id, handle)}
          />
        ))}
      </div>

      {submitError ? (
        <p className="mx-5 mb-4 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-red-800 text-sm">
          <AlertCircle className="mt-px h-4 w-4 shrink-0" />
          <span>{submitError}</span>
        </p>
      ) : null}

      <div className="flex flex-none items-center justify-between border-neutral-100 border-t p-4">
        <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} isLoading={isSubmitting}>
          Approve &amp; book
        </Button>
      </div>
    </>
  );
};

type SectionHandle = {
  /** Validates; resolves to the override (or `null` for none) or `"invalid"`. */
  submit: () => Promise<
    WorkplaceRequestWorkflowOverrides[string] | null | "invalid"
  >;
};

interface AttendeesFieldProps {
  form: UseFormReturn<ReservationOverrideFormValues>;
}

/**
 * Everyone the booking invites — people from the directory and guests named
 * by address — as one list of chips.
 *
 * The form keeps the two apart because the override is written that way, but
 * the approver reads them as one guest list: the people were a closed "2 users
 * selected" summary while the guests sat beside it as chips, which hid exactly
 * who from the organization was being invited. An `attendees` override
 * replaces the whole list, so both halves are always written back together.
 */
const AttendeesField: React.FC<AttendeesFieldProps> = ({ form }) => {
  const userIds = form.watch("attendeeUserIds");
  const emails = form.watch("attendeeEmails");

  const handleChange = (values: string[]) => {
    const options = { shouldDirty: true };
    form.setValue(
      "attendeeUserIds",
      values.filter((value) => !isGuestAttendee(value)),
      options
    );
    form.setValue("attendeeEmails", values.filter(isGuestAttendee), options);
  };

  return (
    <ReservationAttendeesInput
      values={[...userIds, ...emails]}
      onChange={handleChange}
    />
  );
};

interface ReservationSectionProps {
  workflow: WorkplaceRequestReservationTicketWorkflow;
  showHeading: boolean;
  officeId: string;
  fallbackTz: string;
  offices: Office[];
  isBufferEnabled: boolean;
  onRegister: (handle: SectionHandle | null) => void;
}

const ReservationSection: React.FC<ReservationSectionProps> = ({
  workflow,
  showHeading,
  officeId,
  fallbackTz,
  offices,
  isBufferEnabled,
  onRegister
}) => {
  const resolved = workflow.resolved;
  const [isFinderOpen, setIsFinderOpen] = useState(false);
  const [pickedResource, setPickedResource] = useState<IResource | null>(null);

  const requestOffice = offices.find((o) => o.id === officeId);
  const form = useForm<ReservationOverrideFormValues>({
    resolver: zodResolver(reservationOverrideSchema),
    // Provisional zone; the effect below re-derives once the resource's office
    // is known.
    defaultValues: resolvedToFormValues(
      resolved,
      requestOffice?.time_zone ?? fallbackTz
    )
  });

  // The prefilled id is only an id; the resource behind it says whether the
  // room-only fields apply, gives the picker its label and — through its
  // office — the zone the times are edited in.
  const resourceId = form.watch("resourceId");
  const { resources } = useResourcesByIds(resourceId ? [resourceId] : []);
  const resource =
    pickedResource?.id === resourceId ? pickedResource : (resources[0] ?? null);
  const isRoom = resource?.type === "meeting_room";

  // A reservation lives in its resource's office, so its times are that
  // office's wall-clock — not the approver's, who may be several zones away.
  // Until the resource is known, the request's office is the next best guess.
  const office =
    offices.find((o) => o.id === resource?.office_id) ?? requestOffice;
  const tz = office?.time_zone ?? fallbackTz;

  // The zone can settle after the first render (offices and the resource load
  // async). Until the approver has touched anything, re-derive the wall-clock
  // from the resolved instants so the prefilled times match the final zone.
  useEffect(() => {
    if (form.formState.isDirty) return;
    form.reset(resolvedToFormValues(resolved, tz));
  }, [form, resolved, tz]);

  useEffect(() => {
    onRegister({
      submit: async () => {
        const isValid = await form.trigger();
        if (!isValid) return "invalid";
        return formValuesToOverride(form.getValues(), resolved, tz);
      }
    });
    return () => onRegister(null);
  }, [onRegister, form, resolved, tz]);

  const handleSelectResource = (picked: IResource) => {
    form.setValue("resourceId", picked.id, {
      shouldValidate: true,
      shouldDirty: true
    });
    setPickedResource(picked);
    setIsFinderOpen(false);
  };

  return (
    <div className="flex flex-col gap-4">
      {showHeading ? (
        <h3 className="font-medium text-neutral-900 text-sm">
          {workflow.name || "Reservation"}
        </h3>
      ) : null}

      {!resolved ? (
        <p className="rounded-lg bg-amber-50 p-3 text-amber-800 text-xs">
          Tactic couldn't work out this reservation from the request — a form
          field it relies on may have been removed. Fill in the details to book
          it, or cancel and approve without one.
        </p>
      ) : null}

      <HookFormResourcePickerFieldInput
        form={form}
        name="resourceId"
        label="Room or desk"
        isRequired
        icon={<Armchair className="h-4 w-4" />}
        placeholder="Pick a room or desk from the floorplan"
        clearLabel="Remove room or desk"
        onOpen={() => setIsFinderOpen(true)}
        getSelectedLabel={(value) =>
          value ? (resource ? resource.name : "Selected room or desk") : null
        }
        clearValue=""
      />
      <ResourceFinderModal
        isOpen={isFinderOpen}
        onClose={() => setIsFinderOpen(false)}
        initialOfficeId={officeId}
        lockOffice={!!officeId}
        onSelect={handleSelectResource}
      />

      <HookFormAsyncUserSelectInput
        form={form}
        name="organizerId"
        label="Organizer"
        isRequired
        placeholder="Pick the organizer"
      />

      <ReservationPeriodsField
        form={form}
        hasRecurrence={!!resolved?.recurrence}
        timeZoneNote={`Times are in ${
          office ? `${office.name}'s time zone (${tz})` : tz
        }.`}
      />

      {isRoom ? (
        <>
          <HookFormTextInput
            form={form}
            name="title"
            label="Title"
            placeholder="Reservation title"
          />
          <HookFormTextEditorInput
            form={form}
            name="description"
            label="Description"
            formatting="basic"
            enterInsertsLineBreak
          />
          <AttendeesField form={form} />
          <HookFormToggleInput
            form={form}
            name="onlineMeeting"
            label="Add an online meeting link"
          />
        </>
      ) : null}

      {isBufferEnabled ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <HookFormTextInput
            form={form}
            name="bufferBefore"
            label="Buffer before (minutes)"
            type="number"
            min={0}
            max={RESERVATION_BUFFER_MAX_MINUTES}
            step={1}
          />
          <HookFormTextInput
            form={form}
            name="bufferAfter"
            label="Buffer after (minutes)"
            type="number"
            min={0}
            max={RESERVATION_BUFFER_MAX_MINUTES}
            step={1}
          />
        </div>
      ) : null}
    </div>
  );
};
